import { ListItemText, ListItem, Button, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import type { CSSObject } from 'tss-react';
import { useTranslation } from 'react-i18next';
import {
  NotificationOption,
  NotificationSetting,
} from 'features/notifications/notifications.types';
import { type ReactElement } from 'react';
import { NotificationSettingTypes } from 'features/notifications/notifications.constants';

type RegionEditorListItemProps = {
  regionSetting: NotificationSetting;
  onEdit: () => void;
};

type SecondaryTextProps = {
  setting: NotificationOption;
};

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) => ({
  root: {
    backgroundColor: theme.palette.background.secondary,
    borderRadius: Number(theme.shape.borderRadius) * 1.34,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  } as CSSObject,
  itemText: {
    margin: 0,
    lineHeight: 1.5,
  },
  fontMedium: {
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

const SecondaryText = (props: SecondaryTextProps): ReactElement[] | null => {
  const { setting } = props;
  const { t } = useTranslation();

  if (setting === NotificationSettingTypes.all) {
    return null;
  }

  const el = [];

  if (
    setting === NotificationSettingTypes.allWithSilent ||
    setting === NotificationSettingTypes.onlyNewWithSilent
  ) {
    el.push(
      <Typography key="all" variant="body1" color="textSecondary">
        {t('notifications.secondary.includeSilent')}
      </Typography>,
    );
  }

  if (
    setting === NotificationSettingTypes.onlyNew ||
    setting === NotificationSettingTypes.onlyNewWithSilent
  ) {
    el.push(
      <Typography key="only-new" variant="body1" color="textSecondary">
        {t('notifications.secondary.firstOnly')}
      </Typography>,
    );
  }

  return el;
};

export const RegionEditorListItem = (
  props: RegionEditorListItemProps,
): JSX.Element => {
  const { regionSetting, onEdit } = props;
  const { classes } = useStyles();
  const { displayName } = regionSetting.region;
  const { t } = useTranslation();

  return (
    <ListItem
      alignItems="flex-start"
      disableGutters
      className={classes.root}
      aria-label={displayName}
      secondaryAction={
        <Button
          variant="text"
          size="large"
          color="inherit"
          onClick={onEdit}
          data-testid={`edit-${displayName}`}
        >
          {t('common.edit')}
        </Button>
      }
    >
      <ListItemText
        className={classes.itemText}
        primary={displayName}
        secondary={<SecondaryText setting={regionSetting.setting} />}
        slotProps={{
          primary: { className: classes.fontMedium },
        }}
      />
    </ListItem>
  );
};
